/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';

import { AutocompleteFormField } from '@components/form-fields/AutocompleteFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ProposalPolicyPersonDrawer from '@features/production/proposal/drawers/proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { MAIN_THEME } from '@styles';
import { removeObjectAtIndex } from '@utils/helper-utils';

import {
  useGetBusinessPartnerPersons,
  useGetPersonAddressList
} from '../amendment.slice';
import { FormValues } from '../types';

import { useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

type Status = 'NEW' | 'IN_PROGRESS' | 'CONFIRMED' | 'CANCELLED';

export type AmendmentBeneficiaryRow = {
  id: string; // PersonId
  code?: string | null;
  fullName?: string | null;
  name?: string | null; // convenient display field
  addressId: string;
  addressDetails: string;
  beneficiaryPercentage: number | string;
};

type Props = {
  primaryBPId: string; // MainBusinessPartnerID
  lineCode?: string;
  status: Status;
  amendmentId: string;
  maxNumber: number; // Amendment.PolicyID.PlanID.BeneficiaryMaxNumber
  personsOptions: Record<string, string>;
  setPersonsOptions: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
};

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '33px 23px 23px',
    marginBottom: '14px',
    position: 'relative'
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '2%',
    alignItems: 'center',
    '& > :nth-child(1), & > :nth-child(2)': {
      flexBasis: '40%'
    },
    '& > :nth-child(3)': {
      flexBasis: '20%'
    }
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: '20px auto 0',
    display: 'block'
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: '8px',
    right: '0',
    margin: 0
  },
  clearBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 !important'
  }
}));

const formatAddress = (it: any) => {
  const { building, street, city } = it?.address || {};
  const country = it?.country?.title;
  const zone = it?.zone?.title;

  return [building, street, zone, city, country].filter(Boolean).join(', ');
};

const formatPersonLabel = (code?: string | null, fullName?: string | null) => {
  const fn = fullName ?? '';
  const c = code ?? '';
  if (c) return `${c} - ${fn}`;

  return fn;
};

const BeneficiariesWidgetAtom: React.FC<Props> = ({
  primaryBPId,
  lineCode,
  status,
  amendmentId,
  maxNumber,
  personsOptions,
  setPersonsOptions
}) => {
  const { classes } = useStyles();

  const isDisabled = status === 'CONFIRMED' || status === 'CANCELLED';

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    handleBlur
  } = useFormikContext<FormValues>();

  const beneficiaries =
    (values as any)?.beneficiaries ?? ([] as AmendmentBeneficiaryRow[]);

  const beneficiaryErrors = ((errors as any)?.beneficiaries ?? {}) as any;
  const beneficiaryTouched = ((touched as any)?.beneficiaries ?? {}) as any;

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState<number | null>(
    null
  );
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState<
    number | null
  >(null);

  const [addressOptions, setAddressOptions] = useState<
    Record<number, Record<string, string>>
  >({});

  const [personsQuery] = useGetBusinessPartnerPersons();
  const [getPersonAddressListLazy] = useGetPersonAddressList();

  useEffect(() => {
    const addrMap: Record<number, Record<string, string>> = {};
    (beneficiaries as AmendmentBeneficiaryRow[]).forEach((row, idx) => {
      if (row.addressId && row.addressDetails) {
        addrMap[idx] = { [row.addressId]: row.addressDetails };
      }
    });
    setAddressOptions(addrMap);
  }, [beneficiaries]);

  const FetchAddresses = async (personId: string) => {
    const res = await getPersonAddressListLazy({
      variables: { selectedPersonID: personId },
      errorPolicy: 'all'
    });
    const items = res?.data?.salesforce?.getPersonAddressList?.items || [];
    const map: Record<string, string> = {};
    let primary = '';
    let primaryText = '';

    items.forEach((it: any) => {
      const addr = formatAddress(it);
      map[it.id] = addr;
      if (it.isPrimary && !primary) {
        primary = it.id;
        primaryText = addr;
      }
    });

    // Fallback to first address if no primary marked
    if (!primary && items[0]) {
      primary = items[0].id;
      map[primary] = formatAddress(items[0]);
      primaryText = map[primary];
    }

    return { map, primary, primaryText };
  };

  const handlePersonDrawerSuccess = async (
    rowIndex: number,
    personId: string,
    fullName: string
  ) => {
    const label = formatPersonLabel(undefined, fullName);

    setPersonsOptions((prev) => ({
      ...prev,
      [personId]: label
    }));

    await setFieldValue(`beneficiaries[${rowIndex}].fullName`, fullName, false);
    await setFieldValue(`beneficiaries[${rowIndex}].id`, personId, false);
    const { primary, map, primaryText } = await FetchAddresses(
      String(personId)
    );

    setAddressOptions((prev) => ({
      ...prev,
      [rowIndex]: map
    }));

    await setFieldValue(
      `beneficiaries[${rowIndex}].addressId`,
      primary || '',
      false
    );
    await setFieldValue(
      `beneficiaries[${rowIndex}].addressDetails`,
      primaryText || '',
      false
    );

    setNewPersonDrawerOpen(null);
  };

  const handleAddressDrawerSuccess = async (
    rowIndex: number,
    addressId: string,
    addressText: string
  ) => {
    const personId = beneficiaries[rowIndex].id;
    setAddressOptions((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], [addressId]: addressText }
    }));
    await setFieldValue(
      `beneficiaries[${rowIndex}].addressId`,
      addressId,
      false
    );
    await setFieldValue(
      `beneficiaries[${rowIndex}].addressDetails`,
      addressText,
      false
    );
    const insureds = (values as any)?.insureds || [];
    for (let i = 0; i < insureds.length; i++) {
      if (insureds[i].id === personId) {
        await setFieldValue(`insureds[${i}].addressId`, addressId, false);
        await setFieldValue(
          `insureds[${i}].addressDetails`,
          addressText,
          false
        );
      }
    }

    if ((values as any)?.payerOwner?.payer?.id === personId) {
      await setFieldValue('payerOwner.payer.addressId', addressId, false);
      await setFieldValue(
        'payerOwner.payer.addressDetails',
        addressText,
        false
      );
    }

    if ((values as any)?.payerOwner?.owner?.id === personId) {
      await setFieldValue('payerOwner.owner.addressId', addressId, false);
      await setFieldValue(
        'payerOwner.owner.addressDetails',
        addressText,
        false
      );
    }

    for (let i = 0; i < beneficiaries.length; i++) {
      if (i !== rowIndex && beneficiaries[i].id === personId) {
        await setFieldValue(`beneficiaries[${i}].addressId`, addressId, false);
        await setFieldValue(
          `beneficiaries[${i}].addressDetails`,
          addressText,
          false
        );
      }
    }

    setNewAddressDrawerOpen(null);
  };

  const validatePercentagesOnBlur = () => {
    const rows: AmendmentBeneficiaryRow[] =
      ((values as any)?.beneficiaries as AmendmentBeneficiaryRow[]) || [];

    if (!rows.length) return;

    const invalidRows: number[] = [];
    rows.forEach((row, idx) => {
      const val = Number(row.beneficiaryPercentage);
      if (Number.isNaN(val) || val < 0 || val > 100) {
        invalidRows.push(idx);
      }
    });

    if (invalidRows.length) {
      rows.forEach((_, idx) => {
        const path = `beneficiaries[${idx}].beneficiaryPercentage`;
        if (invalidRows.includes(idx)) {
          setFieldError(path, 'Invalid Beneficiary Percentage');
        } else {
          setFieldError(path, '');
        }
      });

      return;
    }

    const total = rows.reduce(
      (sum, row) => sum + (Number(row.beneficiaryPercentage) || 0),
      0
    );
    const normalizedTotal = Math.round(total * 100) / 100;

    if (normalizedTotal !== 100) {
      rows.forEach((_, idx) => {
        const path = `beneficiaries[${idx}].beneficiaryPercentage`;
        setFieldError(path, 'Beneficiary percentages shall sum to 100%');
      });
    } else {
      rows.forEach((_, idx) => {
        const path = `beneficiaries[${idx}].beneficiaryPercentage`;
        setFieldError(path, '');
      });
    }
  };

  const onAddRow = () => {
    if (isDisabled || beneficiaries.length >= maxNumber) return;

    const next: AmendmentBeneficiaryRow = {
      id: '',
      code: '',
      fullName: '',
      name: '',
      addressId: '',
      addressDetails: '',
      beneficiaryPercentage: 0
    };

    const newValues = [...beneficiaries, next];
    setFieldValue('beneficiaries', newValues, false);
  };

  const onDeleteRow = (idx: number) => {
    if (isDisabled) return;

    const nv = removeObjectAtIndex(beneficiaries, idx);
    setFieldValue('beneficiaries', nv, false);

    setAddressOptions((prev) => {
      const updated: Record<number, Record<string, string>> = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (k < idx) updated[k] = prev[k];
        else if (k > idx) updated[k - 1] = prev[k];
      });

      return updated;
    });
  };
  const handleBeneficiaryChange = async (v: string, idx: number) => {
    await setFieldValue(`beneficiaries[${idx}].id`, v, true);

    if (!v) {
      setAddressOptions((prev) => ({
        ...prev,
        [idx]: {}
      }));
      await setFieldValue(`beneficiaries[${idx}].addressId`, '', false);
      await setFieldValue(`beneficiaries[${idx}].addressDetails`, '', false);

      return;
    }

    const { map, primary, primaryText } = await FetchAddresses(v);
    setAddressOptions((prev) => ({
      ...prev,
      [idx]: map
    }));

    await setFieldValue(
      `beneficiaries[${idx}].addressId`,
      primary ?? '',
      false
    );
    await setFieldValue(
      `beneficiaries[${idx}].addressDetails`,
      primaryText ?? '',
      false
    );
  };

  const renderRow = (row: AmendmentBeneficiaryRow, idx: number) => {
    const rowErrors = beneficiaryErrors?.[idx];
    const rowTouched = beneficiaryTouched?.[idx];

    const selectedOthers = beneficiaries.filter(
      (_: any, i: number) => i !== idx
    );
    const takenIds = new Set(
      selectedOthers.map((r: AmendmentBeneficiaryRow) => r.id).filter(Boolean)
    );

    const labelFromPool =
      row.id && personsOptions[row.id] ? personsOptions[row.id] : '';
    const fallbackLabel =
      row.name || formatPersonLabel(row.code, row.fullName) || '';

    const currentLabel = labelFromPool || fallbackLabel;

    const initialOptions: Record<string, string> = {};

    if (row.id && currentLabel) {
      initialOptions[row.id] = currentLabel;
    }

    Object.entries(personsOptions || {}).forEach(([id, label]) => {
      if (id === row.id) return;
      if (takenIds.has(id)) return;
      initialOptions[id] = label;
    });

    return (
      <div key={idx} className={classes.container}>
        <div className={classes.clearBtn}>
          {!isDisabled && beneficiaries.length >= 1 && (
            <button
              type="button"
              className={classes.removeBtn}
              onClick={() => onDeleteRow(idx)}
            >
              <ClearIcon fontSize="small" />
            </button>
          )}
        </div>

        <div className={classes.fieldRow}>
          <WithLinkFormField
            linkText={isDisabled ? '' : 'Add Person'}
            onLinkClick={() => setNewPersonDrawerOpen(idx)}
          >
            <AutocompleteFormField
              name={`beneficiaries[${idx}].id`}
              title="Beneficiary*"
              placeholder="Beneficiary"
              value={row.id}
              error={rowTouched?.id ? rowErrors?.id : ''}
              material
              disabled={isDisabled}
              onChange={(v) => handleBeneficiaryChange(v, idx)}
              onBlur={(_, e) => handleBlur(e)}
              initialOptions={initialOptions}
              fetchOptions={async (
                searchKeyword
              ): Promise<Record<string, string>> => {
                if (!primaryBPId) return {};
                const res = await personsQuery({
                  variables: {
                    mainbusinessPartnerID: primaryBPId,
                    searchKeyword: (searchKeyword ?? '').trim()
                  }
                });

                const items =
                  res?.data?.salesforce?.getBusinessPartnerPersons || [];

                const selectedIds = new Set(
                  beneficiaries
                    .filter((_: any, i: number) => i !== idx)
                    .map((r: AmendmentBeneficiaryRow) => r.id)
                    .filter(Boolean) as string[]
                );

                const filtered = items.filter(
                  (it: any) => !selectedIds.has(it.id)
                );

                const options: Record<string, string> = {};
                filtered.forEach((it: any) => {
                  const label = formatPersonLabel(it.code, it.fullName);
                  options[it.id] = label;
                });

                setPersonsOptions((prev) => ({ ...prev, ...options }));

                const current = beneficiaries[idx] as AmendmentBeneficiaryRow;
                if (current?.id) {
                  const matched = items.find((it: any) => it.id === current.id);
                  if (matched) {
                    await setFieldValue(
                      `beneficiaries[${idx}].code`,
                      matched.code ?? '',
                      false
                    );
                    await setFieldValue(
                      `beneficiaries[${idx}].fullName`,
                      matched.fullName ?? '',
                      false
                    );
                    await setFieldValue(
                      `beneficiaries[${idx}].name`,
                      formatPersonLabel(matched.code, matched.fullName),
                      false
                    );
                  }
                }

                return options;
              }}
            />
          </WithLinkFormField>

          <WithLinkFormField
            linkText={isDisabled ? '' : 'Add Address'}
            onLinkClick={() => setNewAddressDrawerOpen(idx)}
          >
            <SelectFormField
              name={`beneficiaries[${idx}].addressId`}
              title="Beneficiary Address*"
              placeholder="Beneficiary Address"
              value={row.addressId}
              error={rowTouched?.addressId ? (rowErrors?.addressId as any) : ''}
              disabled
              material
              onChange={(v) =>
                setFieldValue(`beneficiaries[${idx}].addressId`, v, true)
              }
              selectOptions={addressOptions[idx] || {}}
            />
          </WithLinkFormField>

          <PercentageFormField
            name={`beneficiaries[${idx}].beneficiaryPercentage`}
            title="Percentage*"
            placeholder="Percentage"
            value={row.beneficiaryPercentage}
            error={
              rowTouched?.beneficiaryPercentage
                ? rowErrors?.beneficiaryPercentage || ''
                : ''
            }
            onBlur={(e: any) => {
              handleBlur(e);
              validatePercentagesOnBlur();
            }}
            onChange={(e) => {
              setFieldValue(
                `beneficiaries[${idx}].beneficiaryPercentage`,
                e.target.value,
                true
              );
            }}
            disabled={isDisabled}
            material
          />
        </div>
      </div>
    );
  };

  return (
    <WidgetPaper>
      <WidgetSection
        title="Beneficiary"
        hasTitleSpecificDesign={false}
        useSeparator={false}
      >
        {(beneficiaries as AmendmentBeneficiaryRow[])?.map((row, idx) =>
          renderRow(row, idx)
        )}

        {newPersonDrawerOpen !== null && (
          <ProposalPolicyPersonDrawer
            open
            onClose={() => setNewPersonDrawerOpen(null)}
            section="beneficiary"
            lineExternalCode={lineCode}
            onSubmitSuccess={(person) => {
              const fullName = `${person.code} - ${person.fullName}`;
              handlePersonDrawerSuccess(
                newPersonDrawerOpen,
                person.id,
                fullName
              );
            }}
            mainBusinessPartnerId={primaryBPId}
          />
        )}

        {newAddressDrawerOpen !== null && (
          <PersonAddressDrawer
            open
            isProposal
            onClose={() => setNewAddressDrawerOpen(null)}
            personId={beneficiaries[newAddressDrawerOpen].id}
            onSubmitSuccess={(addrId, addr) =>
              handleAddressDrawerSuccess(newAddressDrawerOpen, addrId, addr)
            }
            onSuccess={() => undefined}
          />
        )}

        {!isDisabled && beneficiaries.length < maxNumber && (
          <button type="button" className={classes.addBtn} onClick={onAddRow}>
            <AddCircleOutlineIcon fontSize="small" color="error" />
          </button>
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export { BeneficiariesWidgetAtom };
